import React, { useEffect, useState } from 'react';
import { Grid, Stack } from '@mui/material';
import { DeleteForeverOutlined } from '@mui/icons-material';
import { Button, ButtonColorProps, ButtonVariantProps } from '../../../../../components/atoms/Button';
import { ImagePopup, ImagePopupSlide } from '../../../../../components/atoms/ImagePopup';
import { PartTypeActionSearch } from '../../../../../components/molecules/PartTypeActionSearch';
import { PartTypeActionNote } from '../../../../../components/molecules/PartTypeActionNote';
import { useGetUploadedImages } from '../../../../../queries/uploadedfiles-query';
import { useGetPartsLogAgainstService } from '../../../../../queries/part-query';
import { CustomPermission, IconColorProps, PartTypeActionLiveSearchItem, PartTypeActionLiveSearchItemCategoryCode } from '../../../../../@types';
import { Folders } from '../../../../../@types/uploaded-files.type';
import { isEmptyString } from '../../../../../utils/common';
import { PartActionCleanedCardProps } from './PartActionCleanedCard.props';
import { 
  DefaultCard, 
  StyledCardContent,
  CardTitle,
  CloseWrapper,
  InnerWrapper,
  OuterWrapper
} from '../PartAction.styles';
import { SelectMenu } from '../../../../../components/atoms/SelectMenu';
import { MerchantDamagePartSerialMenuData, SerailizedPartsList } from '../../../../../@types/part.type';
import { PartLocationTypeCode, PartTypeCategory, SerializedDropDownOtherOption } from '../../../../../configs/enums';
import { COLORS } from '../../../../../configs/colors';
import { useGetUserSystemPermissions } from '../../../../../queries/user-query';
import { Loader } from '../../../../../components/atoms/Loader';
import { Typography, TypographyVariantProps } from '../../../../../components/atoms/Typography';
import { validateInteractionForms, getFormIsRepeatableStatus, blockInteractionAvailable, getBlockInteractionMessage } from '../../../../../utils/interactions';
import { FormInterceptor } from '../FormInterceptor';

export const PartActionCleanedCard: React.FC<PartActionCleanedCardProps> = ({
  service,
  value,
  helperText,
  title,
  onChange,
  onClose,
  setHelperText,
  isMobileView,
  interceptedFormsObject,
  setInterceptedFormsObject,
  repeatableFormData,
  setRepeatableFormData,
  setMerchantDamageObjectParcer,
  partActionData,
  isInteractionSubmiting,
  submitInteractionForms
}) => {
  const [isSearchMode, setIsSearchMode] = useState(true);
  const [showSerialSearch, setShowSerialSearch] = useState(false);
  const [partImages, setPartImages] = useState<Array<ImagePopupSlide>>([]);
  const [serializedParts, setSerializedParts] = useState<SerailizedPartsList[]>([]);
  const [isPartTypeActionSearch, setIsPartTypeActionSearch] = useState<boolean>(false);
  const [isSerialDropDown, setIsSerialDropDown] = useState<boolean>(true);
  const [permissions, setPermissions] = useState<CustomPermission[]>();
  const [locationInteractionSubmitting, setLocationInteractionSubmitting] = useState(false);

  const uploadedImagesQuery = useGetUploadedImages(value.partTypeId || 0, Folders.PartTypesImages);
  const getPartsLogAgainstServiceQuery = useGetPartsLogAgainstService(service.id, PartLocationTypeCode.Node);
  const getUserPermissionsQuery = useGetUserSystemPermissions();

  useEffect(() => {
    getUserPermissionsQuery.data && setPermissions(getUserPermissionsQuery.data);
  }, [getUserPermissionsQuery.data]);

  useEffect(() => {
    setIsSearchMode(isEmptyString(value.name));
    setShowSerialSearch(value.category === PartTypeActionLiveSearchItemCategoryCode.Serialised);
    setIsPartTypeActionSearch(value.category === PartTypeActionLiveSearchItemCategoryCode.Serialised);
    value.partTypeId && uploadedImagesQuery.refetch();
  }, [value.name, value.category, value.partTypeId]);

  useEffect(() => {
    if (uploadedImagesQuery.data) {
      const images: Array<ImagePopupSlide> = uploadedImagesQuery.data.map((obj) => ({
        url: obj.url
      }));
      setPartImages(images);
    }
  }, [uploadedImagesQuery.data]);

  useEffect(() => {
    if (getPartsLogAgainstServiceQuery?.data) {
      const serializedPartItems = getPartsLogAgainstServiceQuery?.data || [];
      setSerializedParts(serializedPartItems);
    }
  }, [getPartsLogAgainstServiceQuery?.data]);

  const handlePartTypeActionSearch = async (liveSearchSelectedItem: PartTypeActionLiveSearchItem) => {
    setHelperText({
      ...helperText,
      in: { 
        ...helperText.in,
        partTypeHelperText: '',
        partSerialHelperText: ''
      },
    });

    if (liveSearchSelectedItem.serial1) {
      onChange({
        outValue: value,
        inValue: value,
        value:{
          ...value,
          id: value.id,
          partTypeId: liveSearchSelectedItem.partTypeId,
          partId: liveSearchSelectedItem.partId,
          name: liveSearchSelectedItem.partTypeName,
          category: liveSearchSelectedItem.categoryCode,
          condition: liveSearchSelectedItem.conditionCode,
          conditionName: liveSearchSelectedItem?.partCondition?.name,
          serial: liveSearchSelectedItem.serial1,
          location: liveSearchSelectedItem.locationName,
          sw: liveSearchSelectedItem.softwareVersion,
          hw: liveSearchSelectedItem.hardwareVersion,
          fw: liveSearchSelectedItem.firmwareVersion,
          ft: liveSearchSelectedItem.fleetTagName
        }
        
      });
    } else {
      onChange({
        outValue: value,
        inValue: value,
        value:{
          ...value,
          id: value.id,
          partTypeId: liveSearchSelectedItem.partTypeId,
          ns_partId: liveSearchSelectedItem.partId,
          name: liveSearchSelectedItem.partTypeName,
          category: liveSearchSelectedItem.categoryCode,
          condition: liveSearchSelectedItem.conditionCode,
          conditionName: liveSearchSelectedItem?.partCondition?.name,
          location: liveSearchSelectedItem.locationName,
          serial: '',
          sw: liveSearchSelectedItem.softwareVersion,
          hw: liveSearchSelectedItem.hardwareVersion,
          fw: liveSearchSelectedItem.firmwareVersion,
          ft: liveSearchSelectedItem.fleetTagName
        }       
      });
    }
  };


  const handlePartTypeSerialSearch = async (partData: PartTypeActionLiveSearchItem) => {
    setHelperText({
      ...helperText,
      in: { 
        ...helperText.in,
        partTypeHelperText: '',
        partSerialHelperText: ''
      },
    });

    onChange({
      outValue: value,
      inValue: value,
      value:{
        ...value,
        ns_partId: NaN,
        partId: partData.partId,
        serial: partData.serial1,
        location: partData.locationName,
        condition: partData.conditionCode,
        conditionName: partData?.partCondition?.name,
        sw: partData.softwareVersion,
        hw: partData.hardwareVersion,
        fw: partData.firmwareVersion,
        ft: partData.fleetTagName
      }
    });
  };

  const handleSerializedPartSelection = (val: string) => {
    if (val === SerializedDropDownOtherOption.Other) {
      setIsPartTypeActionSearch(true);
      setIsSerialDropDown(false);
    } else {
      const result = serializedParts.filter((part) => part.value === val);
      onChange({
        outValue: value,
        inValue: value,
        value:{
          ...value,
          id: value.id,
          name: result[0].name,
          partTypeId: result[0].partTypeId,
          partId: result[0].partId,
          category: result[0].category,
          condition: result[0].condition,
          conditionName: result[0].conditionName,
          serial: result[0].serial,
          location: result[0].location,
          sw: result[0].sw,
          hw: result[0].hw,
          fw: result[0].fw,
          ft: result[0].ft
        }
      });
      setIsSerialDropDown(false);
    }
  };
  
  const submitInteractions = async() => {
    if (interceptedFormsObject?.forms && interceptedFormsObject?.forms?.length > 0) {
      const { hasErrorFields, hasByPassedFields, blockInteractionBypassed, updatedForms } = validateInteractionForms(interceptedFormsObject, service, getUserPermissionsQuery.data || []);
      if (updatedForms?.length > 0 && (hasErrorFields || hasByPassedFields || blockInteractionBypassed)) {
        setInterceptedFormsObject({ ...interceptedFormsObject, forms: updatedForms });
      };
      if (hasErrorFields) {
        return;
      } else {
        const updatedFormsObject = { ...interceptedFormsObject, forms: updatedForms };
        setLocationInteractionSubmitting(true);
        await submitInteractionForms(updatedFormsObject);
        setLocationInteractionSubmitting(false);
      }
    }
  };

  return (
    <DefaultCard>
      <StyledCardContent isMobileView={isMobileView}>
        <Stack sx={{ backgroundColor: COLORS.White }}>
          <CardTitle>{title}</CardTitle>
        </Stack>
        {partActionData?.executionCompleted ?
          <Stack justifyContent="center" alignItems="center">
            <Typography variant={TypographyVariantProps.Subtitle1} textColor={COLORS.Gray}>
              Part Action Submitted
            </Typography>
          </Stack>
          : !interceptedFormsObject?.forms || !interceptedFormsObject?.forms?.length ?
            <OuterWrapper>
              <CloseWrapper onClick={onClose} isMobileView={isMobileView}>
                <DeleteForeverOutlined color={IconColorProps.Error} fontSize={isMobileView ? 'large' : 'medium'} />
              </CloseWrapper>
              <InnerWrapper isMobileView={isMobileView} spacing={1}>
                <Stack direction="row" justifyContent="space-between" spacing={1} marginTop={1} width="100%">
                  {isSerialDropDown && !value.partTypeId &&
              <SelectMenu id="serialized-parts"
                label="Serialized Parts" 
                items={serializedParts} 
                onChange={(value) => handleSerializedPartSelection(value)}
              />
                  }
                  <Stack direction="column" spacing={2}width="100%">
                    {(isPartTypeActionSearch || value.actionId) &&
                <PartTypeActionSearch
                  serviceData={service}
                  locationId={service.subLocationNodeId || service.mainLocationNodeId}
                  serialiseType={PartTypeCategory.Consumable}
                  title="Part Type Search"
                  value={value.name || ''}
                  error={!!helperText.in.partTypeHelperText}
                  helperText={helperText.in.partTypeHelperText}
                  onChange={handlePartTypeActionSearch}
                  searchType={showSerialSearch || value.actionId && value.category !== PartTypeCategory.Consumable ? 'part-type' : 'part-and-part-type'}
                />
                    }
                    {(showSerialSearch || value.actionId && value.category !== PartTypeCategory.Consumable) &&
                <PartTypeActionSearch
                  title="Part Serial Search"
                  serialiseType={value.category}
                  partTypeId={`${value.id}`}
                  serviceData={service}
                  value={value.serial || ''}
                  error={!!helperText.in.partSerialHelperText}
                  helperText={helperText.in.partSerialHelperText}
                  onChange={handlePartTypeSerialSearch}
                  searchType="part"
                />
                    }
                  </Stack>
                  {isSearchMode || <ImagePopup title="Photos" images={partImages} />}
                </Stack>
                {isSearchMode || 
            <Grid size={{ xs: 12 }}>
              <PartTypeActionNote
                value={value?.notes || ''}
                onChange={(note) => onChange({
                  outValue: value,
                  inValue: value,
                  value:{
                    ...value,
                    notes: note
                  }       
                }, true)} 
              />
            </Grid>
                }
              </InnerWrapper>
            </OuterWrapper>
            :
            <Grid>
              {interceptedFormsObject?.forms?.map((form, index) =>
                <FormInterceptor
                  key={index}
                  service={service}
                  value={form}
                  onChange={(editedForms) => {
                    if (repeatableFormData.templateId === 0 && repeatableFormData.templateTitle === '') {
                      const updatedForms = interceptedFormsObject?.forms.map((formData) => formData.id === editedForms.id ? editedForms : formData);
                      setInterceptedFormsObject({
                        ...interceptedFormsObject,
                        forms: updatedForms
                      });
                    }
                  }}
                  onDamagedPartSelect={(Objectkey: string, objectValue: FormData | number | MerchantDamagePartSerialMenuData) => {
                    setMerchantDamageObjectParcer(prevState => ({ ...prevState, [Objectkey]: objectValue }));
                  }}
                  partActionData={partActionData}
                  onAddForm={(templateId, templateTitle) => setRepeatableFormData({ templateId, templateTitle })}
                  isRepeatable={getFormIsRepeatableStatus(form, interceptedFormsObject.forms)}
                  forms={interceptedFormsObject.forms}
                  isMobileView={isMobileView}
                />
              )}
              <br/>
              <Grid>
                <Stack direction="row" spacing={2} alignItems="center" ml={2}>
                  <Button
                    variant={ButtonVariantProps.Primary}
                    color={ButtonColorProps.Success}
                    onClick={submitInteractions}
                    disabled={(isInteractionSubmiting && locationInteractionSubmitting) || blockInteractionAvailable(permissions || [], interceptedFormsObject?.forms)}
                    startIcon={isInteractionSubmiting && locationInteractionSubmitting ? <Loader size={20} /> : null}
                  >
                    Submit
                  </Button>
                  {blockInteractionAvailable(permissions || [], interceptedFormsObject?.forms || []) && <Typography textColor={COLORS.Red}>{getBlockInteractionMessage(interceptedFormsObject?.forms || [])}</Typography>}
                </Stack>
              </Grid>
            </Grid>
        }
      </StyledCardContent>
    </DefaultCard>
  );
};